/**
 * PlaybackPopout - /playback/:recordingId
 *
 * Rich playback surface designed to open in a popout window
 * (window.open with popup features). Bare layout - no sidebar, no
 * top navigation, dark by default. Renders:
 *   - Call metadata card (caller, agent, queue, duration, direction, etc.)
 *   - DualPlayer (voice + screen, if screen recording linked)
 *   - Toggle: mute the voice track (for QA reviewing screen only)
 *   - QA evaluation panel (visible only to admin / supervisor / qa roles)
 *
 * Called from the Recordings list "Popout" button:
 *     window.open('/playback/<id>', 'cx-playback-<id>',
 *       'width=1400,height=900,menubar=no,toolbar=no,location=no,status=no')
 *
 * Introduced: 2026-07-02 (Phase 2.4 - popout playback + QA integration).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { recordingsApi, screenRecordingsApi, qaApi } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import EvaluationViewerModal from './qa/EvaluationViewerModal';


// Roles allowed to see the QA evaluation panel (write access).
const QA_ROLES = new Set(['admin', 'supervisor', 'qa']);
// 2026-07-16: roles allowed to VIEW a submitted evaluation. Backend enforces
// agent-only-own-recording rule + hides drafts from agents.
const QA_VIEW_ROLES = new Set(['admin', 'supervisor', 'qa', 'qa_manager', 'viewer', 'agent']);


function fmtDuration(sec) {
  if (sec == null) return '-';
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function fmtBytes(b) {
  if (b == null) return '-';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtTs(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}


export default function PlaybackPopout() {
  const { recordingId } = useParams();
  const { user } = useAuth();

  const [recording, setRecording] = useState(null);
  const [screenMeta, setScreenMeta] = useState(null);
  // Multi-monitor (2026-09-10): extra screens (screen_index >= 1) beyond the
  // primary. Rendered as additional synced videos next to the primary.
  const [extraScreens, setExtraScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Voice track mute toggle (QA sometimes wants to review the screen
  // recording without the audio distracting).
  const [voiceMuted, setVoiceMuted] = useState(false);

  // Evaluation panel state
  const [showEval, setShowEval] = useState(false);

  const canEvaluate = user && QA_ROLES.has(user.role);
  const canView     = user && QA_VIEW_ROLES.has(user.role);

  // 2026-07-16: Track whether a submitted evaluation exists for this
  // recording. Populated by an effect after `recording` loads. Used to
  // gate the "👁 View evaluation" button that's available to ALL viewer
  // roles (backend also enforces agent-only-own-recording).
  const [hasSubmittedEval, setHasSubmittedEval] = useState(false);
  const [showViewer, setShowViewer] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rec = await recordingsApi.get(recordingId);
        if (cancelled) return;
        setRecording(rec);
        if (rec?.call_id) {
          try {
            // Multi-monitor (2026-09-10): fetch ALL screens for the call.
            const all = await screenRecordingsApi.getAllByCall(rec.call_id);
            if (!cancelled && Array.isArray(all) && all.length > 0) {
              const sorted = [...all].sort(
                (a, b) => (a.screen_index || 0) - (b.screen_index || 0),
              );
              setScreenMeta(sorted[0]);            // primary (index 0)
              setExtraScreens(sorted.slice(1));    // index 1+
            } else if (!cancelled) {
              // Fallback to the single-screen endpoint (older data / safety).
              try {
                const s = await screenRecordingsApi.getByCall(rec.call_id);
                if (!cancelled) setScreenMeta(s);
              } catch (e2) {
                if (!cancelled && e2?.status !== 404) console.warn('screen lookup error', e2);
              }
            }
          } catch (err) {
            // 404 / no by-call-all -> try the single endpoint before giving up.
            if (!cancelled) {
              try {
                const s = await screenRecordingsApi.getByCall(rec.call_id);
                if (!cancelled) setScreenMeta(s);
              } catch (e2) {
                if (!cancelled && e2?.status !== 404) console.warn('screen lookup error', e2);
              }
            }
          }
        }
      } catch (err) {
        if (!cancelled) setError(err?.detail || err?.message || 'load_failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [recordingId]);

  useEffect(() => {
    if (recording) {
      const label = recording.caller_id || recording.did || recording.call_id || recordingId;
      document.title = `Play: ${label} - ContinuumCX Playback`;
    } else {
      document.title = 'ContinuumCX Playback';
    }
  }, [recording, recordingId]);

  // 2026-07-16: probe whether this recording has any submitted eval. This
  // gates the "View evaluation" button. Backend endpoint returns list of
  // EvaluationOut; we filter out drafts here as well (defense in depth).
  useEffect(() => {
    let cancelled = false;
    if (!recordingId || !canView) {
      setHasSubmittedEval(false);
      return;
    }
    (async () => {
      try {
        const rows = await qaApi.listForRecording(recordingId);
        if (cancelled) return;
        const submitted = (rows || []).filter(e => e.status && e.status !== 'draft');
        setHasSubmittedEval(submitted.length > 0);
      } catch (_) {
        if (!cancelled) setHasSubmittedEval(false);
      }
    })();
    return () => { cancelled = true; };
  }, [recordingId, canView]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">
        <div className="text-lg">Loading recording...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">
        <div className="text-lg text-red-400">Error: {String(error)}</div>
      </div>
    );
  }
  if (!recording) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">
        <div className="text-lg">Recording not found.</div>
      </div>
    );
  }

  // 2026-07-15 pm: also treat failed/aborted with file_size>0 as playable.
  const hasScreen = !!(
    screenMeta && screenMeta.id && (
      screenMeta.status === 'complete' ||
      (['failed','aborted'].includes(screenMeta.status) && (screenMeta.file_size || 0) > 0)
    )
  );

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="max-w-[1600px] mx-auto p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold">
              Call Playback
              {recording.direction && (
                <span className="ml-3 text-sm font-normal opacity-80 capitalize">
                  &middot; {recording.direction}
                </span>
              )}
            </h1>
            <div className="text-sm text-neutral-400 mt-1">
              {fmtTs(recording.started_at)} &middot; duration {fmtDuration(recording.duration_sec)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setVoiceMuted(v => !v)}
              className={
                'px-3 py-1.5 rounded-md text-sm border ' +
                (voiceMuted
                  ? 'bg-amber-900/40 border-amber-500 text-amber-200'
                  : 'bg-neutral-800 border-neutral-700 text-neutral-100 hover:bg-neutral-700')
              }
              title="Toggle voice audio track"
            >
              {voiceMuted ? 'Voice muted' : 'Voice on'}
            </button>
            {hasSubmittedEval && canView && (
              <button
                onClick={() => setShowViewer(true)}
                className="px-3 py-1.5 rounded-md text-sm border bg-neutral-800 border-neutral-700 text-neutral-100 hover:bg-neutral-700"
                title="View submitted QA evaluation for this call"
              >
                👁 View evaluation
              </button>
            )}
            {canEvaluate && (
              <button
                onClick={() => setShowEval(v => !v)}
                className={
                  'px-3 py-1.5 rounded-md text-sm border ' +
                  (showEval
                    ? 'bg-continuum-600 border-continuum-500 text-white'
                    : 'bg-neutral-800 border-neutral-700 text-neutral-100 hover:bg-neutral-700')
                }
              >
                {showEval ? 'Close Evaluation' : (hasSubmittedEval ? 'Edit evaluation' : 'Click to evaluate')}
              </button>
            )}
            <a
              href={recordingsApi.downloadUrl(recording.id)}
              download
              className="px-3 py-1.5 rounded-md text-sm bg-neutral-800 border border-neutral-700 hover:bg-neutral-700"
            >
              ↓ Voice
            </a>
            {hasScreen && (
              <a
                href={`/api/recordings/${recording.id}/download-combined`}
                download
                className="px-3 py-1.5 rounded-md text-sm bg-neutral-800 border border-neutral-700 hover:bg-neutral-700"
                title="Download voice + screen muxed as MP4 (may take a few seconds to build)"
              >
                ↓ MP4 (voice+screen)
              </a>
            )}
          </div>
        </div>

        {/* Metadata row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
          <MetaCard label="Caller" value={recording.caller_id || '-'} />
          <MetaCard label="Agent" value={recording.agent_name || recording.agent_extension || '-'} />
          <MetaCard label="Extension" value={recording.agent_extension || '-'} />
          <MetaCard label="Queue" value={recording.queue_name || recording.queue_id || '-'} />
          <MetaCard label="DID" value={recording.did || '-'} />
          <MetaCard label="Size" value={fmtBytes(recording.file_size)} />
        </div>

        {/* Player + evaluation panel side-by-side when evaluating */}
        <div className={showEval ? 'grid grid-cols-1 xl:grid-cols-3 gap-4' : ''}>
          <div className={showEval ? 'xl:col-span-2' : ''}>
            <DualPlayerPopout
              recording={recording}
              screenMeta={hasScreen ? screenMeta : null}
              extraScreens={hasScreen ? extraScreens : []}
              voiceMuted={voiceMuted}
            />
          </div>
          {showEval && canEvaluate && (
            <div className="bg-neutral-900 rounded-md p-4 border border-neutral-800 max-h-[85vh] overflow-y-auto">
              <EvaluationPanel recording={recording} />
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="mt-4 text-xs text-neutral-500">
          Recording ID: {recording.id}
          {recording.call_id && <> &middot; Call ID: {recording.call_id}</>}
          {hasScreen && <> &middot; Screen offset: {Math.round(screenMeta.sync_offset_ms || 0)}ms</>}
        </div>

        {/* 2026-07-16: read-only evaluation viewer for ALL roles. */}
        <EvaluationViewerModal
          open={showViewer}
          onClose={() => setShowViewer(false)}
          recordingId={recording.id}
          recording={recording}
          recordingLabel={
            [recording.agent_extension, recording.agent_name, recording.direction,
             recording.started_at ? new Date(recording.started_at).toLocaleString() : null]
              .filter(Boolean).join(' · ')
          }
        />
      </div>
    </div>
  );
}


function MetaCard({ label, value }) {
  return (
    <div className="bg-neutral-900 rounded-md p-2 border border-neutral-800 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-sm truncate" title={String(value)}>{value}</div>
    </div>
  );
}


// -----------------------------------------------------------------------
// Popout DualPlayer - same sync logic as the inline one, bigger UI,
// voice mute toggle, screen video with native controls.
// -----------------------------------------------------------------------
//
// DualPlayerPopout — audio (local) + screen (Azure remote) synchronized playback.
//
// 2026-07-15 update: added a "pre-buffer both, release together" gate so
// screen-vs-voice startup drift is eliminated. Previously the audio would
// start instantly while the video was still buffering from Azure/CDN, and
// the timeupdate drift correction couldn't catch up because the video's
// buffered window at the target time was empty (freeze frames).
//
// New behavior:
//   1. On mount, both media begin loading with preload="auto".
//   2. Audio's native controls are disabled AND a full-width "Preparing
//      playback…" overlay is shown until BOTH tracks report readyState >=
//      HAVE_FUTURE_DATA (3). For voice-only recordings we only wait on
//      audio.
//   3. When ready, the overlay is removed, native audio controls re-enabled.
//   4. When the user presses Play on the audio element, the video is
//      imperatively started in the same tick with the current sync_offset
//      applied. Both begin together.
//   5. During playback the existing timeupdate drift-correction still
//      re-syncs the video if it falls > 200ms out.
//   6. On any seek/waiting/stall we transparently re-gate (mini-buffering
//      overlay) so a mid-track buffer stall doesn't desync everything.
//
function DualPlayerPopout({ recording, screenMeta, extraScreens = [], voiceMuted }) {
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const hasScreen = !!screenMeta;
  const offsetSec = (screenMeta?.sync_offset_ms ?? 0) / 1000;
  // Multi-monitor (2026-09-10): imperative refs to each EXTRA screen <video>,
  // driven off the SAME audio scrubber as the primary.
  const extraVideoRefs = useRef([]);
  const extras = Array.isArray(extraScreens) ? extraScreens : [];

  // Startup gate: true until both tracks are ready-enough to play smoothly.
  const [ready, setReady] = useState(false);
  // Mid-playback buffer stall gate: true while any track is buffering.
  const [buffering, setBuffering] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = !!voiceMuted;
    }
  }, [voiceMuted]);

  // --- Startup readiness gate --------------------------------------------
  // We consider a track "ready enough" at readyState >= 3 (HAVE_FUTURE_DATA).
  // Requiring 4 (HAVE_ENOUGH_DATA) is stricter but sometimes never fires for
  // streamed content — 3 is a good balance.
  useEffect(() => {
    const a = audioRef.current;
    const v = hasScreen ? videoRef.current : null;
    if (!a) return;

    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      const aOk = a.readyState >= 3;
      const vOk = !hasScreen || (v && v.readyState >= 3);
      if (aOk && vOk) {
        setReady(true);
      }
    };
    const onA = () => check();
    const onV = () => check();
    a.addEventListener('canplay', onA);
    a.addEventListener('canplaythrough', onA);
    a.addEventListener('loadeddata', onA);
    if (v) {
      v.addEventListener('canplay', onV);
      v.addEventListener('canplaythrough', onV);
      v.addEventListener('loadeddata', onV);
    }
    // First synchronous check in case both are already ready by mount.
    check();
    return () => {
      cancelled = true;
      a.removeEventListener('canplay', onA);
      a.removeEventListener('canplaythrough', onA);
      a.removeEventListener('loadeddata', onA);
      if (v) {
        v.removeEventListener('canplay', onV);
        v.removeEventListener('canplaythrough', onV);
        v.removeEventListener('loadeddata', onV);
      }
    };
  }, [hasScreen, recording.id, screenMeta?.id]);

  // --- Play/pause + drift sync -------------------------------------------
  useEffect(() => {
    if (!hasScreen) return;
    const a = audioRef.current;
    const v = videoRef.current;
    if (!a || !v) return;

    // Multi-monitor: keep every extra screen aligned to the same audio clock,
    // each with its OWN sync offset. Best-effort; an extra never blocks.
    const syncExtras = () => {
      extras.forEach((meta, i) => {
        const ev = extraVideoRefs.current[i];
        if (!ev) return;
        const eoff = (meta?.sync_offset_ms ?? 0) / 1000;
        const etarget = Math.max(0, a.currentTime - eoff);
        if (Math.abs(ev.currentTime - etarget) > 0.2) ev.currentTime = etarget;
      });
    };
    const playExtras = () => {
      extras.forEach((meta, i) => {
        const ev = extraVideoRefs.current[i];
        if (!ev) return;
        const eoff = (meta?.sync_offset_ms ?? 0) / 1000;
        const etarget = Math.max(0, a.currentTime - eoff);
        if (Math.abs(ev.currentTime - etarget) > 0.05) ev.currentTime = etarget;
        if (ev.paused) ev.play().catch(() => {});
      });
    };
    const pauseExtras = () => {
      extras.forEach((_, i) => {
        const ev = extraVideoRefs.current[i];
        if (ev && !ev.paused) ev.pause();
      });
    };

    const onTimeUpdate = () => {
      const target = Math.max(0, a.currentTime - offsetSec);
      if (Math.abs(v.currentTime - target) > 0.2) {
        v.currentTime = target;
      }
      syncExtras();
    };
    const onPlay = () => {
      // Align video to audio.currentTime - offset BEFORE calling play(),
      // then start both. Because we've already waited for readyState>=3 on
      // both, this .play() should not stall.
      const target = Math.max(0, a.currentTime - offsetSec);
      if (Math.abs(v.currentTime - target) > 0.05) {
        v.currentTime = target;
      }
      if (v.paused) v.play().catch(() => {});
      playExtras();
    };
    const onPause = () => { if (!v.paused) v.pause(); pauseExtras(); };
    const onSeeked = () => { onTimeUpdate(); syncExtras(); };

    // Mid-playback buffering: if either track waits/stalls, show a mini
    // spinner and pause the other so they don't desync.
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);

    a.addEventListener('timeupdate', onTimeUpdate);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('seeked', onSeeked);
    a.addEventListener('waiting', onWaiting);
    a.addEventListener('playing', onPlaying);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('playing', onPlaying);

    return () => {
      a.removeEventListener('timeupdate', onTimeUpdate);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('seeked', onSeeked);
      a.removeEventListener('waiting', onWaiting);
      a.removeEventListener('playing', onPlaying);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('playing', onPlaying);
    };
  }, [hasScreen, offsetSec, extras.length]);

  return (
    <div className="space-y-3">
      {hasScreen ? (
        <div className="relative">
          <div className="text-xs text-neutral-500 mb-1">Screen recording</div>
          <video
            ref={videoRef}
            src={screenRecordingsApi.streamUrl(screenMeta.id)}
            controls={false}
            muted
            playsInline
            preload="auto"
            className="w-full rounded-md border border-neutral-800 bg-black max-h-[70vh]"
          />
          {/* Multi-monitor (2026-09-10): show the label only when >1 screen. */}
          {extras.length > 0 && (
            <div className="text-[11px] text-neutral-500 mt-1">Screen 1 of {extras.length + 1}</div>
          )}
          {(!ready || buffering) && (
            <div
              className="absolute inset-0 mt-6 flex items-center justify-center bg-black/60 rounded-md pointer-events-none"
              aria-live="polite"
            >
              <div className="flex items-center gap-3 text-neutral-200 text-sm">
                <span className="inline-block w-4 h-4 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
                {ready ? 'Buffering…' : 'Preparing playback…'}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-neutral-900 border border-neutral-800 rounded-md p-6 text-center text-neutral-400">
          No screen recording linked to this call.
        </div>
      )}

      {/* Multi-monitor (2026-09-10): additional screens (screen_index >= 1),
          each synced to the same voice scrubber. */}
      {hasScreen && extras.map((meta, i) => (
        <div className="relative" key={meta.id || i}>
          <div className="text-xs text-neutral-500 mb-1">
            Screen {(meta.screen_index || i + 1) + 1} of {extras.length + 1}
          </div>
          <video
            ref={(el) => { extraVideoRefs.current[i] = el; }}
            src={screenRecordingsApi.streamUrl(meta.id)}
            controls={false}
            muted
            playsInline
            preload="auto"
            className="w-full rounded-md border border-neutral-800 bg-black max-h-[70vh]"
          />
        </div>
      ))}
      <div>
        <div className="text-xs text-neutral-500 mb-1 flex items-center justify-between">
          <span>Voice recording {voiceMuted && '(muted)'}</span>
          {!ready && (
            <span className="text-amber-300">
              ⚪ Preparing… (please wait before pressing play)
            </span>
          )}
        </div>
        <audio
          ref={audioRef}
          src={recordingsApi.streamUrl(recording.id)}
          // Native controls are hidden until both tracks are ready—this
          // physically prevents an early click from starting audio-only.
          controls={ready}
          preload="auto"
          className="w-full"
        >
          Your browser does not support audio playback.
        </audio>
        {!ready && (
          <div className="mt-2 h-9 rounded-md bg-neutral-800 border border-neutral-700 flex items-center justify-center text-xs text-neutral-400">
            <span className="inline-block w-3 h-3 mr-2 border-2 border-neutral-500 border-t-transparent rounded-full animate-spin" />
            Preparing playback…
          </div>
        )}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// EvaluationPanel - Phase QA-1 (2026-07-02).
//
// Fetches active forms, lets the evaluator pick one, renders a dynamic
// form for the questions in that form, and saves as draft / submits.
// -----------------------------------------------------------------------
function EvaluationPanel({ recording }) {
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [form, setForm] = useState(null);
  const [existingEvals, setExistingEvals] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [answers, setAnswers] = useState({});
  const [evalNotes, setEvalNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [fs, evs] = await Promise.all([
          qaApi.listForms(true),
          qaApi.listForRecording(recording.id).catch(() => []),
        ]);
        if (cancelled) return;
        setForms(fs || []);
        setExistingEvals(evs || []);
        const draft = (evs || []).find(e => e.status === 'draft');
        if (draft) {
          setSelectedFormId(draft.form_id);
          setEvaluation(draft);
          setEvalNotes(draft.evaluator_notes || '');
          const a = {};
          for (const ans of (draft.answers || [])) {
            a[ans.question_id] = { answer: ans.value?.answer, comment: ans.comment || '' };
          }
          setAnswers(a);
        } else if ((fs || []).length > 0) {
          setSelectedFormId(fs[0].id);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [recording.id]);

  useEffect(() => {
    if (!selectedFormId) { setForm(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const f = await qaApi.getForm(selectedFormId);
        if (!cancelled) setForm(f);
      } catch (err) {
        console.warn('form fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedFormId]);

  const setAnswer = (qid, patch) => {
    setAnswers(prev => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...patch } }));
  };

  const buildAnswersPayload = () => {
    if (!form) return [];
    const out = [];
    for (const s of (form.sections || [])) {
      for (const q of (s.questions || [])) {
        const a = answers[q.id];
        if (!a || a.answer === undefined || a.answer === null || a.answer === '') continue;
        out.push({
          question_id: q.id,
          value: { answer: a.answer },
          comment: a.comment || null,
        });
      }
    }
    return out;
  };

  const saveDraft = async () => {
    setSaving(true); setSaveError(null);
    try {
      const payload = {
        evaluator_notes: evalNotes || null,
        answers: buildAnswersPayload(),
      };
      if (evaluation) {
        const updated = await qaApi.updateEvaluation(evaluation.id, payload);
        setEvaluation(updated);
      } else {
        const created = await qaApi.createEvaluation({
          form_id: form.id,
          recording_id: recording.id,
          ...payload,
        });
        setEvaluation(created);
      }
    } catch (err) {
      setSaveError(err?.detail || err?.message || 'save_failed');
    } finally {
      setSaving(false);
    }
  };

  const submitEval = async () => {
    setSaving(true); setSaveError(null);
    try {
      const payload = {
        evaluator_notes: evalNotes || null,
        answers: buildAnswersPayload(),
      };
      let e = evaluation;
      if (!e) {
        e = await qaApi.createEvaluation({
          form_id: form.id,
          recording_id: recording.id,
          ...payload,
        });
      } else {
        e = await qaApi.updateEvaluation(e.id, payload);
      }
      const finalE = await qaApi.submitEvaluation(e.id);
      setEvaluation(finalE);
    } catch (err) {
      setSaveError(err?.detail || err?.message || 'submit_failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-neutral-400">Loading QA forms...</div>;
  }

  if (evaluation && evaluation.status === 'submitted') {
    return (
      <div className="space-y-3">
        <div className="text-lg font-semibold">Evaluation Submitted</div>
        <div className="bg-neutral-950 border border-neutral-800 rounded-md p-3">
          <div className="text-sm text-neutral-400">Form: {evaluation.form_name}</div>
          <div className="text-2xl font-bold mt-2">
            {evaluation.overall_score != null
              ? `${evaluation.overall_score.toFixed(1)}%`
              : '-'}
            {evaluation.passed === true && (
              <span className="ml-2 text-sm text-emerald-400">PASS</span>
            )}
            {evaluation.passed === false && (
              <span className="ml-2 text-sm text-red-400">FAIL</span>
            )}
            {evaluation.auto_fail_triggered && (
              <span className="ml-2 text-xs text-amber-400">(auto-fail)</span>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-1">
            Submitted {new Date(evaluation.submitted_at).toLocaleString()}
          </div>
        </div>
        {evaluation.evaluator_notes && (
          <div className="bg-neutral-950 border border-neutral-800 rounded-md p-3">
            <div className="text-xs text-neutral-500 mb-1">Notes</div>
            <div className="text-sm text-neutral-300 whitespace-pre-wrap">{evaluation.evaluator_notes}</div>
          </div>
        )}
      </div>
    );
  }

  if (forms.length === 0) {
    return (
      <div className="space-y-2">
        <div className="text-lg font-semibold">Call Evaluation</div>
        <div className="text-sm text-neutral-400">
          No QA forms configured. An admin must create one under
          {' '}<span className="text-neutral-200">Admin &gt; QA Forms</span>.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-lg font-semibold">Call Evaluation</div>

      <div>
        <label className="text-xs text-neutral-500 block mb-1">Form</label>
        <select
          value={selectedFormId}
          onChange={e => setSelectedFormId(e.target.value)}
          className="w-full bg-neutral-950 border border-neutral-800 rounded-md p-2 text-sm"
          disabled={!!evaluation}
        >
          {forms.map(f => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
      </div>

      {form && (form.sections || []).map(s => (
        <div key={s.id} className="bg-neutral-950 border border-neutral-800 rounded-md p-3">
          <div className="font-medium text-sm mb-2">{s.title}</div>
          {s.description && <div className="text-xs text-neutral-500 mb-2">{s.description}</div>}
          <div className="space-y-3">
            {s.questions.map(q => (
              <QuestionRow
                key={q.id}
                question={q}
                answer={answers[q.id]}
                onChange={patch => setAnswer(q.id, patch)}
              />
            ))}
          </div>
        </div>
      ))}

      <div>
        <label className="text-xs text-neutral-500 block mb-1">Evaluator notes</label>
        <textarea
          value={evalNotes}
          onChange={e => setEvalNotes(e.target.value)}
          rows={3}
          className="w-full bg-neutral-950 border border-neutral-800 rounded-md p-2 text-sm"
          placeholder="Overall coaching notes, disputes, callouts..."
        />
      </div>

      {saveError && (
        <div className="text-sm text-red-400">Error: {String(saveError)}</div>
      )}

      <div className="flex gap-2">
        <button
          onClick={saveDraft}
          disabled={saving || !form}
          className="px-3 py-1.5 rounded-md text-sm bg-neutral-800 border border-neutral-700 hover:bg-neutral-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save draft'}
        </button>
        <button
          onClick={submitEval}
          disabled={saving || !form}
          className="px-3 py-1.5 rounded-md text-sm bg-emerald-700 border border-emerald-500 hover:bg-emerald-600 disabled:opacity-50"
        >
          {saving ? '...' : 'Submit evaluation'}
        </button>
      </div>

      {existingEvals.filter(e => e.status !== 'draft').length > 0 && (
        <div className="text-xs text-neutral-500 mt-3">
          {existingEvals.filter(e => e.status !== 'draft').length} prior evaluation(s) exist for this call.
        </div>
      )}
    </div>
  );
}


function QuestionRow({ question, answer = {}, onChange }) {
  const q = question;
  return (
    <div>
      <div className="text-sm mb-1">
        {q.text}
        {q.auto_fail && <span className="ml-2 text-[10px] text-amber-400">(auto-fail)</span>}
        {q.required && <span className="ml-1 text-red-400">*</span>}
      </div>
      {q.kind === 'yes_no' && (
        <div className="flex gap-2">
          {['yes', 'no', 'na'].map(v => (
            <button
              key={v}
              onClick={() => onChange({ answer: v })}
              className={
                'px-2 py-1 rounded text-xs border ' +
                (answer.answer === v
                  ? (v === 'yes' ? 'bg-emerald-800 border-emerald-500'
                    : v === 'no' ? 'bg-red-800 border-red-500'
                    : 'bg-neutral-700 border-neutral-500')
                  : 'bg-neutral-950 border-neutral-800 hover:bg-neutral-800')
              }
            >{v === 'na' ? 'N/A' : v.toUpperCase()}</button>
          ))}
        </div>
      )}
      {q.kind === 'scale_1_5' && (
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => onChange({ answer: n })}
              className={
                'w-8 h-8 rounded text-sm border ' +
                (answer.answer === n
                  ? 'bg-continuum-600 border-continuum-400 text-white'
                  : 'bg-neutral-950 border-neutral-800 hover:bg-neutral-800')
              }
            >{n}</button>
          ))}
        </div>
      )}
      {q.kind === 'multi_choice' && (
        <div className="flex flex-wrap gap-1">
          {(q.choices || []).map(c => (
            <button
              key={c.value}
              onClick={() => onChange({ answer: c.value })}
              className={
                'px-2 py-1 rounded text-xs border ' +
                (answer.answer === c.value
                  ? 'bg-continuum-600 border-continuum-400 text-white'
                  : 'bg-neutral-950 border-neutral-800 hover:bg-neutral-800')
              }
              title={c.score != null ? `Score: ${c.score}` : ''}
            >{c.label || c.value}</button>
          ))}
        </div>
      )}
      {q.kind === 'free_text' && (
        <textarea
          value={answer.answer || ''}
          onChange={e => onChange({ answer: e.target.value })}
          rows={2}
          className="w-full bg-neutral-950 border border-neutral-800 rounded p-2 text-xs"
        />
      )}
    </div>
  );
}
